'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface SubscriptionTier {
  duration: string;
  price: number;
  savings?: string;
  popular?: boolean;
}

interface Product {
  id: string;
  sku: string;
  name: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
  ingredients: string[];
  doctorApproved: boolean;
  subscriptionTiers: SubscriptionTier[];
}

interface ProductCardProps {
  product: Product;
}

export default function ProductCard({ product }: ProductCardProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedTier, setSelectedTier] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="bg-card border border-border rounded-xl overflow-hidden shadow-elevation-md">
        <div className="relative h-64 bg-muted">
          <AppImage
            src={product.imageUrl}
            alt={product.imageAlt}
            fill
            className="object-cover"
          />
        </div>
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                {product.name}
              </h3>
              <p className="text-sm text-muted-foreground">SKU: {product.sku}</p>
            </div>
          </div>
          <p className="text-muted-foreground mb-6 line-clamp-3">
            {product.description}
          </p>
        </div>
      </div>
    );
  }

  const handleTierChange = (index: number) => {
    setSelectedTier(index);
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden shadow-elevation-md transition-smooth hover:shadow-elevation-lg hover:-translate-y-1">
      <div className="relative h-64 bg-muted overflow-hidden">
        <AppImage
          src={product.imageUrl}
          alt={product.imageAlt}
          fill
          className="object-cover"
        />
        {product.doctorApproved && (
          <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-2 bg-success text-success-foreground rounded-lg text-sm font-medium shadow-elevation-md">
            <Icon name="ShieldCheckIcon" size={16} />
            Doctor Approved
          </div>
        )}
      </div>
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
              {product.name}
            </h3>
            <p className="text-sm text-muted-foreground">SKU: {product.sku}</p>
          </div>
        </div>
        <p className="text-muted-foreground mb-6 line-clamp-3">
          {product.description}
        </p>

        <div className="mb-6">
          <h4 className="text-sm font-medium text-foreground mb-3">Key Ingredients:</h4>
          <div className="flex flex-wrap gap-2">
            {product.ingredients.map((ingredient, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-muted text-foreground text-sm rounded-full"
              >
                {ingredient}
              </span>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <h4 className="text-sm font-medium text-foreground mb-3">Choose Your Plan:</h4>
          <div className="space-y-2">
            {product.subscriptionTiers.map((tier, index) => (
              <button
                key={index}
                onClick={() => handleTierChange(index)}
                className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-smooth ${
                  selectedTier === index
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedTier === index
                        ? 'border-primary bg-primary' :'border-muted-foreground'
                    }`}
                  >
                    {selectedTier === index && (
                      <div className="w-2 h-2 bg-primary-foreground rounded-full"></div>
                    )}
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-foreground">{tier.duration}</p>
                    {tier.savings && (
                      <p className="text-xs text-success">{tier.savings}</p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xl font-heading font-semibold text-foreground">
                    ${tier.price}
                  </p>
                  <p className="text-xs text-muted-foreground">per month</p>
                </div>
                {tier.popular && (
                  <span className="absolute -top-2 right-4 px-2 py-1 bg-accent text-accent-foreground text-xs font-medium rounded-full">
                    Most Popular
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <Link
          href={`/multi-tier-checkout?sku=${product.sku}&tier=${selectedTier}`}
          className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium transition-smooth hover:shadow-elevation-md hover:scale-[0.97]"
        >
          <Icon name="ShoppingCartIcon" size={20} />
          Add to Cart
        </Link>
      </div>
    </div>
  );
}